import React, { useState } from "react";
import { toast } from "sonner";
import { Minus, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui-kit";
import { fmtMoney } from "@/components/EventCard";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import type { Booking } from "@/types";

export const GuestsChangeDialog = ({ b, onClose, onDone }: { b: Booking; onClose: () => void; onDone: () => void }) => {
  const { t } = usePrefs();
  const [guests, setGuests] = useState(b.guests);
  const [busy, setBusy] = useState(false);
  const unit = b.price_snapshot?.unit;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${b.booking_id}/change`, { guests });
      toast.success(t(b.status === "pending" ? "b5.changed" : "b5.changeSent"));
      onDone();
      onClose();
    } catch (e) {
      toast.error(errorText(e, t));
    } finally {
      setBusy(false);
    }
  };
  const step = "h-10 w-10 rounded-full border grid place-items-center";
  return (
    <Dialog open onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-[calc(100vw-2rem)] sm:max-w-sm" data-testid="guests-change-dialog">
        <DialogHeader className="text-start"><DialogTitle className="font-display">{t("b5.changeTitle")}</DialogTitle></DialogHeader>
        {b.status === "confirmed" && <p className="text-sm text-muted-foreground">{t("b5.changeNote")}</p>}
        <div className="flex items-center justify-center gap-4 py-2">
          <button data-testid="guests-change-minus" onClick={() => setGuests(Math.max(1, guests - 1))} className={step}><Minus className="h-4 w-4" /></button>
          <span data-testid="guests-change-value" className="w-8 text-center text-2xl font-black">{guests}</span>
          <button data-testid="guests-change-plus" onClick={() => setGuests(Math.min(50, guests + 1))} className={step}><Plus className="h-4 w-4" /></button>
        </div>
        {unit && (
          <p className="flex justify-between rounded-2xl bg-accent/60 p-3 text-sm">
            <span>{t("b5.newTotal")}</span>
            <b dir="ltr" data-testid="guests-change-total">{fmtMoney({ amount: String(Number(unit.amount) * guests), currency: unit.currency })}</b>
          </p>
        )}
        <button onClick={submit} disabled={busy || guests === b.guests} data-testid="guests-change-submit" className="h-12 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-50">
          {busy ? t("common.loading") : t("b5.send")}
        </button>
      </DialogContent>
    </Dialog>
  );
};
